import events from '../../constants/events';
import { dispatchEvent } from '../../utilities/customEvents';

import { Base } from './Base';

const classes = {
  active: 'active',
  disabled: 'disabled',
  hidden: 'hidden',
  radioContainer: 'radio-container',
  showForm: 'show-form',
  showStatus: 'show-status',
  waiting: 'waiting'
};

const selectors = {
  radioContainer: '.radio-container',
  radioLabel: '.radio-label',
  selectLengthOption: '.select-length__option',
  selectSize: '.select-size'
};

export class InlineAddToCart extends Base {
  constructor(behaviorName, element) {
    super(behaviorName, element);
    this.init();
  }

  init() {
    this.bindButtonHandler();
    this.bindRadioContainerHandlers();
    this.bindVariantChangedListener();
  }

  bindButtonHandler() {
    const { button, hiddenInput, quantity } = this.refs;

    if (button) {
      button.addEventListener('click', (e) => {
        e.preventDefault();

        if (hiddenInput) {
          const variantId = hiddenInput.value;
          const quantityValue = quantity.value;

          this.addToCart(variantId, quantityValue);
        } else {
          this.element.classList.add(classes.showForm);
        }
      });
    }
  }

  bindRadioContainerHandlers() {
    const { form, selectLength } = this.refs;

    if (form) {
      const radioContainers = form.querySelectorAll(selectors.radioContainer);

      // Bind select length handlers if not a radio container
      if (selectLength && !selectLength.classList.contains(classes.radioContainer)) {
        selectLength.querySelectorAll(selectors.selectLengthOption).forEach((selectLengthOption) => {
          selectLengthOption.addEventListener('click', this.handleSelectLengthOption.bind(this));
        });
      }

      // Bind radio container handlers
      radioContainers.forEach((radioContainer) => {
        radioContainer.addEventListener('mouseleave', this.updateHelperText.bind(this));

        radioContainer.querySelectorAll(selectors.radioLabel).forEach((radioLabel) => {
          radioLabel.addEventListener('click', this.handleRadioContainer.bind(this));
          radioLabel.addEventListener('mouseover', this.handleSizeMouseOver.bind(this));
        });
      });
    }
  }

  setHelperTextDefault() {
    const { helperText } = this.refs;

    if (helperText) {
      helperText.innerHTML = helperText.dataset.default;
    }
  }

  setHelperTextDisabled() {
    const { helperText } = this.refs;

    if (helperText) {
      helperText.innerHTML = helperText.dataset.disabled;
    }
  }

  setHelperTextAddToCart() {
    const { helperText } = this.refs;

    if (helperText) {
      helperText.innerHTML = helperText.dataset.addToCart;
    }
  }

  showActiveLengthSelectSize() {
    const { selectLength, form } = this.refs;
    const selectSizes = form.querySelectorAll(selectors.selectSize);

    if (selectLength && form && selectSizes) {
      const selectLengthActive = selectLength.querySelector(selectors.selectLengthOption + '.' + classes.active);

      if (selectLengthActive) {
        const targetId = selectLengthActive.href.split('#')[1];

        selectSizes.forEach((selectSize) => {
          if (selectSize.id === targetId) {
            selectSize.classList.remove(classes.hidden);
          } else {
            selectSize.classList.add(classes.hidden);
          }
        });
      }
    }
  }

  handleSelectLengthOption(e) {
    e.preventDefault();

    if (!e.target.classList.contains(classes.active)) {
      const selectLengthOptions = e.target.parentNode.querySelectorAll(selectors.selectLengthOption);

      selectLengthOptions.forEach((selectLengthOption) => {
        if (selectLengthOption === e.target) {
          selectLengthOption.classList.add(classes.active);
        } else {
          selectLengthOption.classList.remove(classes.active);
        }
      });

      this.resetRadios();
      this.showActiveLengthSelectSize();
    }
  }

  handleRadioContainer(e) {
    const { quantity } = this.refs;
    const radioContainer = e.target.parentNode;

    // Set/unset active class
    radioContainer.querySelectorAll(selectors.radioLabel).forEach((radioLabel) => {
      if (radioLabel != e.target) {
        document.getElementById(radioLabel.getAttribute('for')).checked = false;
        radioLabel.classList.remove(classes.active);
      } else {
        radioLabel.classList.toggle(classes.active);

        if (radioLabel.classList.contains(classes.active)) {
          document.getElementById(radioLabel.getAttribute('for')).checked = true;
        } else {
          document.getElementById(radioLabel.getAttribute('for')).checked = false;
        }
      }
    });

    // Update helper text
    this.updateHelperText(e);

    // Perform add to cart
    if (!e.target.classList.contains(classes.disabled)) {
      const variantId = document.getElementById(e.target.getAttribute('for')).value;
      const quantityValue = quantity.value;

      this.addToCart(variantId, quantityValue);
    }
  }

  handleSizeMouseOver(e) {
    if (e.target.classList.contains(classes.disabled)) {
      this.setHelperTextDisabled();
    } else {
      this.setHelperTextAddToCart();
    }
  }

  updateHelperText(e) {
    const radioContainer = e.target.parentNode;

    if (radioContainer) {
      const activeItem = radioContainer.querySelector('.' + classes.active);

      if (activeItem) {
        if (activeItem.classList.contains(classes.disabled)) {
          this.setHelperTextDisabled();
        } else {
          this.setHelperTextDefault();
        }
      } else {
        this.setHelperTextDefault();
      }
    }
  }

  addToCart(variantId, quantity) {
    if (!this.element.classList.contains(classes.waiting)) {
      this.element.classList.add(classes.waiting);

      fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        body: JSON.stringify({
          quantity: quantity,
          id: variantId
        }),
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
      })
      .then((response) => {
        this.displayStatus(response.status >= 400);

        if (response.status >= 200 && response.status < 300) {
          dispatchEvent(window, events.CART_UPDATED, {
            numberOfItemsAdded: quantity
          });

          response.json().then((json) => {
            document.dispatchEvent(new CustomEvent('cart:added'));
            dispatchEvent(window, events.ITEM_ADDED_WIDGET, json)
          });
        }

        this.element.classList.remove(classes.waiting);
      });
    }
  }

  displayStatus(error = false) {
    const { statusText } = this.refs;

    // Set correct status text
    if (error) {
      statusText.innerHTML = statusText.dataset.error;
    } else {
      statusText.innerHTML = statusText.dataset.default;
    }

    // Show status message
    this.element.classList.add(classes.showForm);
    this.element.classList.add(classes.showStatus);

    // Reset any active radios
    this.resetRadios();

    // Set timeout to remove status message
    setTimeout(() => {
      this.element.classList.remove(classes.showForm);
      this.element.classList.remove(classes.showStatus);
    }, 3000);
  }

  resetRadios() {
    const { form } = this.refs;

    if (form) {
      // Remove any active radio labels and uncheck their inputs
      form.querySelectorAll(selectors.radioLabel).forEach((radioLabel) => {
        radioLabel.classList.remove(classes.active);
        document.getElementById(radioLabel.getAttribute('for')).checked = false;
      });
    }
  }

  bindVariantChangedListener() {
    if (this.element.dataset.listenToVariantChanged === 'true') {
      this.variantChangedHandler = this.handleVariantChanged.bind(this);
      window.addEventListener(events.VARIANT_CHANGED, this.variantChangedHandler);
    }
  }

  handleVariantChanged(e) {
    this.element.classList.remove(classes.showForm);
    this.updateOptions(e);
  }

  updateOptions(e) {
    const { length_links, length_selectors, size_selectors } = e.detail;
    const { options, selectLength } = this.refs;

    let optionsHTML = '';
    let selectLengthHTML = '';

    if (length_links.length > 0) {
      for (let i = 0; i < length_links.length; i++) {
        selectLengthHTML += `<a href="#buy-bar-select-length-${i}" class="select-length__option ${length_links[i].active ? 'active': ''}">${length_links[i].text}</a>`;

        if (length_links[i].size_selectors) {
          optionsHTML += `<div id="buy-bar-select-length-${i}" class="select-size select-size--center radio-container ${length_links[i].active ? '' : 'hidden'}">`;

          for (let j = 0; j < length_links[i].size_selectors.length; j++) {
            optionsHTML += `<input type="radio" name="id" id="buy-bar-inline-add-to-cart-${length_links[i].size_selectors[j].id}" value="${length_links[i].size_selectors[j].id}" class="radio-input" ${length_links[i].size_selectors[j].disabled ? 'disabled="disabled"' : ''}>`;
            optionsHTML += `<label for="buy-bar-inline-add-to-cart-${length_links[i].size_selectors[j].id}" class="radio-label select-size__option ${length_links[i].size_selectors[j].disabled ? 'disabled': ''}">${length_links[i].size_selectors[j].text}</label>`;
          }

          optionsHTML += '</div>';
        }
      }

    } else if (size_selectors.length > 0) {
      optionsHTML += `<div class="select-size select-size--center radio-container">`;

      for (let i = 0; i < size_selectors.length; i++) {
        optionsHTML += `<input type="radio" name="id" id="buy-bar-inline-add-to-cart-${size_selectors[i].id}" value="${size_selectors[i].id}" class="radio-input" ${size_selectors[i].disabled ? 'disabled="disabled"' : ''}>`;
        optionsHTML += `<label for="buy-bar-inline-add-to-cart-${size_selectors[i].id}" class="radio-label select-size__option ${size_selectors[i].disabled ? 'disabled': ''}">${size_selectors[i].text}</label>`;
      }

      optionsHTML += '</div>';
    } else if (length_selectors.length > 0) {
      optionsHTML += `<div class="select-length select-length--center radio-container">`;

      for (let i = 0; i < length_selectors.length; i++) {
        optionsHTML += `<input type="radio" name="id" id="buy-bar-inline-add-to-cart-${length_selectors[i].id}" value="${length_selectors[i].id}" class="radio-input" ${length_selectors[i].disabled ? 'disabled="disabled"' : ''}>`;
        optionsHTML += `<label for="buy-bar-inline-add-to-cart-${length_selectors[i].id}" class="radio-label select-length__option ${length_selectors[i].disabled ? 'disabled': ''}">${length_selectors[i].text}</label>`;
      }

      optionsHTML += '</div>';
    }

    if (options) {
      options.innerHTML = optionsHTML;
    }

    if (selectLength) {
      selectLength.innerHTML = selectLengthHTML;
    }

    this.bindRadioContainerHandlers();
  }

  cleanup() {
    window.removeEventListener(events.VARIANT_CHANGED, this.variantChangedHandler);
  }
}
